import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  NumberInput,
  NumberInputField,
  Spinner,
  Text,
  UnorderedList,
  ListItem,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { format } from "date-fns";
import { FiCalendar, FiExternalLink, FiPlay, FiRefreshCw } from "react-icons/fi";
import { hitBeApi } from "../../../api/api";
import { AiCreditsPaywall } from "../ai/aiFeatureUi";
import CaQuizRunner from "./CaQuizRunner";
import CaAnalytics from "./CaAnalytics";

const SOURCE_LABEL = {
  "the-hindu": "The Hindu",
  "indian-express": "Indian Express",
};

const fmt = (date) => format(date, "yyyy-MM-dd");

/**
 * Current Affairs preparation: calendar → day articles (The Hindu + Indian
 * Express) → MCQ practice strictly from those articles → scores + analytics.
 *
 * Credit state is passed from the parent ExamAi so the single payment modal works.
 */
export default function CurrentAffairs({
  canUseAi = true,
  promptBuyCredits,
  applyCredits,
}) {
  const toast = useToast();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [dayCached, setDayCached] = useState(false);

  const [count, setCount] = useState(5);
  const [rangeFrom, setRangeFrom] = useState(fmt(new Date()));
  const [rangeTo, setRangeTo] = useState(fmt(new Date()));

  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [pastQuizzes, setPastQuizzes] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.600");
  const muted = useColorModeValue("gray.600", "gray.400");
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const headingCol = useColorModeValue("gray.800", "white");
  const relevanceBg = useColorModeValue("purple.50", "purple.900");

  const showError = useCallback(
    (error, fallback) => {
      toast({
        title: fallback,
        description: String(error?.message || "").slice(0, 200) || undefined,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    },
    [toast]
  );

  const loadAnalytics = useCallback(async () => {
    try {
      const res = await hitBeApi("dashboard/exam-ai/current-affairs/analytics", "GET");
      setAnalytics(res?.data?.analytics || null);
    } catch {
      // analytics are best-effort
    }
  }, []);

  const loadPastQuizzes = useCallback(async (dateStr) => {
    try {
      const res = await hitBeApi(
        `dashboard/exam-ai/current-affairs/quizzes?date=${dateStr}`,
        "GET"
      );
      setPastQuizzes(res?.data?.quizzes || []);
    } catch {
      setPastQuizzes([]);
    }
  }, []);

  // Free: show already-cached articles for a date without fetching/charging.
  const loadCachedDay = useCallback(async (dateStr) => {
    try {
      const res = await hitBeApi(
        `dashboard/exam-ai/current-affairs/day?date=${dateStr}`,
        "GET"
      );
      setArticles(res?.data?.articles || []);
      setDayCached(!!(res?.data?.articles || []).length);
    } catch {
      setArticles([]);
      setDayCached(false);
    }
  }, []);

  // Paid: fetch (or force re-fetch) a day's articles via web search.
  const loadDay = useCallback(
    async (dateStr, force = false) => {
      if (!canUseAi) {
        promptBuyCredits?.();
        return;
      }
      setArticlesLoading(true);
      setArticles([]);
      try {
        const res = await hitBeApi(
          "dashboard/exam-ai/current-affairs/day",
          "POST",
          { date: dateStr, force }
        );
        applyCredits?.(res?.data?.userTokenDetails);
        setArticles(res?.data?.articles || []);
        setDayCached(!!res?.data?.cached);
        if (!(res?.data?.articles || []).length) {
          toast({
            title: "No articles found for this date",
            description: "Coverage can be thin for older dates. Try a recent date.",
            status: "info",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        showError(error, "Could not load articles.");
      } finally {
        setArticlesLoading(false);
      }
    },
    [canUseAi, promptBuyCredits, applyCredits, toast, showError]
  );

  // Initial load: today's cached articles + attempts + analytics.
  useEffect(() => {
    const today = fmt(new Date());
    loadAnalytics();
    loadPastQuizzes(today);
    loadCachedDay(today);
  }, [loadAnalytics, loadPastQuizzes, loadCachedDay]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const dateStr = fmt(date);
    setRangeFrom(dateStr);
    setRangeTo(dateStr);
    setActiveQuiz(null);
    setArticles([]);
    setDayCached(false);
    loadPastQuizzes(dateStr);
    loadCachedDay(dateStr); // show cached articles instantly (free) if present
  };

  const generateQuiz = async (dateFrom, dateTo) => {
    if (!canUseAi) {
      promptBuyCredits?.();
      return;
    }
    setQuizLoading(true);
    setActiveQuiz(null);
    try {
      const res = await hitBeApi(
        "dashboard/exam-ai/current-affairs/quiz",
        "POST",
        { dateFrom, dateTo, count: Number(count) || 5 }
      );
      applyCredits?.(res?.data?.userTokenDetails);
      setActiveQuiz(res?.data?.quiz || null);
      loadPastQuizzes(fmt(selectedDate));
    } catch (error) {
      showError(error, "Could not generate quiz.");
    } finally {
      setQuizLoading(false);
    }
  };

  const handleSubmitQuiz = async (answers) => {
    if (!activeQuiz) return;
    setSubmitting(true);
    try {
      const res = await hitBeApi(
        `dashboard/exam-ai/current-affairs/quiz/${activeQuiz._id}/submit`,
        "POST",
        { answers }
      );
      setActiveQuiz(res?.data?.quiz || activeQuiz);
      loadAnalytics();
      loadPastQuizzes(fmt(selectedDate));
    } catch (error) {
      showError(error, "Could not submit quiz.");
    } finally {
      setSubmitting(false);
    }
  };

  const openPastQuiz = async (quizId) => {
    setQuizLoading(true);
    try {
      const res = await hitBeApi(
        `dashboard/exam-ai/current-affairs/quiz/${quizId}`,
        "GET"
      );
      setActiveQuiz(res?.data?.quiz || null);
    } catch (error) {
      showError(error, "Could not open quiz.");
    } finally {
      setQuizLoading(false);
    }
  };

  const selectedStr = fmt(selectedDate);

  return (
    <Box position="relative">
      <AiCreditsPaywall
        show={!canUseAi}
        featureName="Current Affairs"
        onBuy={promptBuyCredits}
        theme="purple"
      />

      <Flex direction={{ base: "column", lg: "row" }} gap={4} align="stretch">
        {/* Left: calendar + quiz controls */}
        <Box w={{ base: "100%", lg: "320px" }} flexShrink={0}>
          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={border}
            borderRadius="2xl"
            p={3}
            boxShadow="sm"
            mb={3}
          >
            <Calendar
              onChange={handleDateChange}
              value={selectedDate}
              maxDate={new Date()}
              className="custom-calendar"
            />
            <Button
              mt={3}
              w="full"
              size="sm"
              colorScheme="purple"
              leftIcon={<FiCalendar />}
              isLoading={articlesLoading}
              onClick={() => loadDay(selectedStr)}
            >
              Load {format(selectedDate, "MMM d, yyyy")}
            </Button>
          </Box>

          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={border}
            borderRadius="2xl"
            p={4}
            boxShadow="sm"
          >
            <Heading size="xs" mb={3} color={headingCol}>
              Generate a quiz
            </Heading>
            <VStack align="stretch" spacing={2}>
              <Box>
                <Text fontSize="xs" color={muted} mb={1}>
                  From
                </Text>
                <Input
                  type="date"
                  size="sm"
                  value={rangeFrom}
                  max={fmt(new Date())}
                  onChange={(e) => setRangeFrom(e.target.value)}
                />
              </Box>
              <Box>
                <Text fontSize="xs" color={muted} mb={1}>
                  To
                </Text>
                <Input
                  type="date"
                  size="sm"
                  value={rangeTo}
                  max={fmt(new Date())}
                  onChange={(e) => setRangeTo(e.target.value)}
                />
              </Box>
              <Box>
                <Text fontSize="xs" color={muted} mb={1}>
                  Number of questions
                </Text>
                <NumberInput
                  size="sm"
                  min={1}
                  max={50}
                  value={count}
                  onChange={(_, n) => setCount(Number.isNaN(n) ? 1 : n)}
                >
                  <NumberInputField />
                </NumberInput>
              </Box>
              <Button
                colorScheme="purple"
                size="sm"
                leftIcon={<FiPlay />}
                isLoading={quizLoading}
                onClick={() => generateQuiz(rangeFrom, rangeTo)}
              >
                Generate from range
              </Button>
              <Text fontSize="xs" color={muted}>
                Questions are generated strictly from the selected dates' articles
                and AI-checked to be answerable from the article alone.
              </Text>
            </VStack>
          </Box>

          {pastQuizzes.length ? (
            <Box
              bg={cardBg}
              borderWidth="1px"
              borderColor={border}
              borderRadius="2xl"
              p={4}
              boxShadow="sm"
              mt={3}
            >
              <Heading size="xs" mb={2} color={headingCol}>
                Attempts including {selectedStr}
              </Heading>
              <VStack align="stretch" spacing={1}>
                {pastQuizzes.map((z) => (
                  <HStack
                    key={String(z._id)}
                    justify="space-between"
                    p={2}
                    borderRadius="md"
                    _hover={{ bg: pageBg }}
                    cursor="pointer"
                    onClick={() => openPastQuiz(z._id)}
                  >
                    <Text fontSize="xs" noOfLines={1}>
                      {z.dateFrom === z.dateTo ? z.dateFrom : `${z.dateFrom}→${z.dateTo}`}
                    </Text>
                    {z.status === "attempted" ? (
                      <Badge
                        colorScheme={z.score / z.total >= 0.5 ? "green" : "red"}
                        fontSize="0.65rem"
                      >
                        {z.score}/{z.total}
                      </Badge>
                    ) : (
                      <Badge fontSize="0.65rem">unattempted</Badge>
                    )}
                  </HStack>
                ))}
              </VStack>
            </Box>
          ) : null}
        </Box>

        {/* Right: articles + quiz + analytics */}
        <Box flex="1" minW={0}>
          {/* Active quiz takes priority */}
          {activeQuiz ? (
            <Box mb={4}>
              <HStack justify="space-between" mb={3}>
                <Heading size="sm" color={headingCol}>
                  Practice quiz
                </Heading>
                <Button size="xs" variant="ghost" onClick={() => setActiveQuiz(null)}>
                  Back to articles
                </Button>
              </HStack>
              <CaQuizRunner
                quiz={activeQuiz}
                onSubmit={handleSubmitQuiz}
                submitting={submitting}
              />
            </Box>
          ) : (
            <Box mb={4}>
              <HStack justify="space-between" mb={3} flexWrap="wrap">
                <Heading size="sm" color={headingCol}>
                  {format(selectedDate, "MMMM d, yyyy")} · UPSC articles
                </Heading>
                {articles.length ? (
                  <HStack spacing={2}>
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="purple"
                      leftIcon={<FiRefreshCw />}
                      isLoading={articlesLoading}
                      onClick={() => loadDay(selectedStr, true)}
                      title="Re-fetch this day's articles (uses credits)"
                    >
                      Refresh
                    </Button>
                    <Button
                      size="xs"
                      colorScheme="purple"
                      leftIcon={<FiPlay />}
                      isLoading={quizLoading}
                      onClick={() => generateQuiz(selectedStr, selectedStr)}
                    >
                      Practice {count} questions
                    </Button>
                  </HStack>
                ) : null}
              </HStack>

              {articlesLoading ? (
                <Flex justify="center" py={12} direction="column" align="center" gap={3}>
                  <Spinner color="purple.500" />
                  <Text fontSize="sm" color={muted}>
                    Fetching from The Hindu &amp; Indian Express…
                  </Text>
                </Flex>
              ) : articles.length ? (
                <>
                  {dayCached ? (
                    <Text fontSize="xs" color={muted} mb={2}>
                      Loaded from cache (no credits used).
                    </Text>
                  ) : null}
                  <VStack align="stretch" spacing={3}>
                    {articles.map((a) => (
                      <Box
                        key={String(a._id || a.title)}
                        bg={cardBg}
                        borderWidth="1px"
                        borderColor={border}
                        borderRadius="xl"
                        p={4}
                        boxShadow="sm"
                      >
                        <HStack mb={1} flexWrap="wrap" spacing={2}>
                          <Badge colorScheme="purple">
                            {SOURCE_LABEL[a.source] || "News"}
                          </Badge>
                          {a.category ? (
                            <Badge variant="subtle">{a.category}</Badge>
                          ) : null}
                          {a.publishedDate ? (
                            <Badge
                              variant="subtle"
                              colorScheme={a.publishedDate === selectedStr ? "green" : "orange"}
                            >
                              {a.publishedDate}
                            </Badge>
                          ) : null}
                        </HStack>
                        <Heading size="sm" mb={1} color={headingCol}>
                          {a.title}
                        </Heading>
                        {a.summary ? (
                          <Text fontSize="sm" color={muted} mb={2} lineHeight="tall">
                            {a.summary}
                          </Text>
                        ) : null}
                        {a.background ? (
                          <Text fontSize="sm" mb={2} lineHeight="tall">
                            <b>Background:</b> {a.background}
                          </Text>
                        ) : null}
                        {a.upscRelevance ? (
                          <Box
                            bg={relevanceBg}
                            borderRadius="md"
                            px={3}
                            py={2}
                            mb={2}
                          >
                            <Text fontSize="sm" lineHeight="tall">
                              <b>UPSC relevance:</b> {a.upscRelevance}
                            </Text>
                          </Box>
                        ) : null}
                        {(a.keyPoints || []).length ? (
                          <UnorderedList fontSize="sm" color={muted} spacing={0.5} pl={4}>
                            {a.keyPoints.map((k, i) => (
                              <ListItem key={i}>{k}</ListItem>
                            ))}
                          </UnorderedList>
                        ) : null}
                        {a.url ? (
                          <HStack mt={2}>
                            <Icon as={FiExternalLink} boxSize={3} color={muted} />
                            <Text
                              as="a"
                              href={a.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              fontSize="xs"
                              color="purple.500"
                              noOfLines={1}
                            >
                              Read original
                            </Text>
                          </HStack>
                        ) : null}
                      </Box>
                    ))}
                  </VStack>
                </>
              ) : (
                <Flex
                  justify="center"
                  py={12}
                  direction="column"
                  align="center"
                  gap={2}
                  bg={cardBg}
                  borderWidth="1px"
                  borderColor={border}
                  borderRadius="xl"
                >
                  <Text fontSize="sm" color={muted} textAlign="center" px={4}>
                    No saved articles for {format(selectedDate, "MMMM d, yyyy")} yet.
                  </Text>
                  <Button
                    size="sm"
                    colorScheme="purple"
                    leftIcon={<FiCalendar />}
                    isLoading={articlesLoading}
                    onClick={() => loadDay(selectedStr)}
                  >
                    Fetch articles
                  </Button>
                </Flex>
              )}
            </Box>
          )}

          <Divider my={4} />
          <CaAnalytics analytics={analytics} />
        </Box>
      </Flex>
    </Box>
  );
}
